import pptxgen from "pptxgenjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { supabase } from "@/integrations/supabase/client";

const COLORS = {
  navy: "1a3a5f",
  gold: "C9A84C",
  teal: "006B6B",
  white: "FFFFFF",
  dark: "1f2937",
  light: "F3F4F6",
  green: "22c55e",
  red: "ef4444",
  amber: "f59e0b",
};

export type ReportMode = "summary" | "detailed";
export type ReportScope = "region" | "nation";
export type ReportFormat = "pptx" | "pdf";

export interface CoverInfo {
  presidentName?: string;
  secretaryName?: string;
  meetingDate?: string; // yyyy-mm-dd
  logoDataUrl?: string; // base64 data url
}

const OFUESI_WEBSITE = "ofuesi.org";

// Lazy-load default OFUESI logo from /ofuesi-logo.png if caller didn't provide one
async function ensureLogo(cover?: CoverInfo): Promise<string | undefined> {
  if (cover?.logoDataUrl) return cover.logoDataUrl;
  try {
    const res = await fetch("/ofuesi-logo.png");
    if (!res.ok) return undefined;
    const blob = await res.blob();
    return await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onerror = reject;
      r.onload = () => resolve(r.result as string);
      r.readAsDataURL(blob);
    });
  } catch { return undefined; }
}

const scopeLabel = (opts: Options) =>
  opts.scope === "nation" ? "National Report" : `${opts.region || "Regional"} Regional Report`;

export interface Options {
  scope: ReportScope;
  mode: ReportMode;
  year: number;
  region?: string;
  format?: ReportFormat;
  cover?: CoverInfo;
  generatedByName?: string;
  generatedById?: string;
  archive?: boolean; // default true
}

const fmtMoney = (n: number) =>
  `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

async function loadData({ scope, year, region }: Options) {
  let q = supabase.from("conference_kit_events" as any).select("*");
  if (scope === "region" && region) q = q.eq("region", region);
  const { data: eventsRaw } = await q;
  const events = ((eventsRaw as any[]) || []).filter((e) => {
    const y = e.conference_year ?? (e.start_date ? new Date(e.start_date).getFullYear() : null);
    return y === year;
  });
  const ids = events.map((e: any) => e.id);

  const [budgetRes, expensesRes, submissionsRes, magazineRes, publishRes, regsRes, feedbackRes] = await Promise.all([
    ids.length ? (supabase as any).from("conference_budget").select("*").in("kit_event_id", ids) : { data: [] },
    ids.length ? (supabase as any).from("conference_expenses").select("*").in("kit_event_id", ids) : { data: [] },
    ids.length ? (supabase as any).from("conference_kit_submissions").select("*").in("kit_event_id", ids) : { data: [] },
    ids.length ? (supabase as any).from("kit_magazine_submissions").select("*").in("kit_event_id", ids) : { data: [] },
    ids.length ? (supabase as any).from("conference_kit_publish_log").select("*").in("kit_event_id", ids) : { data: [] },
    scope === "region" && region
      ? supabase.from("conference_registrations").select("*").eq("region", region)
      : supabase.from("conference_registrations").select("*"),
    scope === "region" && region
      ? supabase.from("conference_feedback").select("*").eq("region", region).eq("conference_year", year)
      : supabase.from("conference_feedback").select("*").eq("conference_year", year),
  ]);

  return {
    events,
    budget: (budgetRes.data || []) as any[],
    expenses: (expensesRes.data || []) as any[],
    submissions: (submissionsRes.data || []) as any[],
    magazine: (magazineRes.data || []) as any[],
    publishLog: (publishRes.data || []) as any[],
    registrations: (regsRes.data || []) as any[],
    feedback: (feedbackRes.data || []) as any[],
  };
}

// region-wise aggregate helper
function aggregateByRegion(data: ReturnType<typeof loadData> extends Promise<infer R> ? R : never) {
  const map: Record<string, {
    region: string;
    registrations: number;
    members: number;
    confirmed: number;
    estimated: number;
    income: number;
    spent: number;
  }> = {};
  data.registrations.forEach((r) => {
    const k = r.region || "Unknown";
    map[k] ||= { region: k, registrations: 0, members: 0, confirmed: 0, estimated: 0, income: 0, spent: 0 };
    map[k].registrations++;
    map[k].members += r.total_members || 1;
    if (r.payment_status === "confirmed") map[k].confirmed++;
  });
  // attach budget / expenses via event -> region
  const eventRegion: Record<string, string> = {};
  data.events.forEach((e: any) => { eventRegion[e.id] = e.region || "Unknown"; });
  data.budget.forEach((b: any) => {
    const k = eventRegion[b.kit_event_id] || "Unknown";
    map[k] ||= { region: k, registrations: 0, members: 0, confirmed: 0, estimated: 0, income: 0, spent: 0 };
    const amt = Number(b.estimated_amount || 0);
    if ((b.category || "").toLowerCase().startsWith("income")) map[k].income += amt;
    else map[k].estimated += amt;
  });
  data.expenses.forEach((e: any) => {
    const k = eventRegion[e.kit_event_id] || "Unknown";
    map[k] ||= { region: k, registrations: 0, members: 0, confirmed: 0, estimated: 0, income: 0, spent: 0 };
    map[k].spent += Number(e.amount || 0);
  });
  return Object.values(map).sort((a, b) => a.region.localeCompare(b.region));
}

interface CoordinatorInfo { name?: string; email?: string; phone?: string; region?: string; }

const titleSlide = (pptx: pptxgen, opts: Options, logo: string | undefined, coordinators: CoordinatorInfo[]) => {
  const s = pptx.addSlide();
  s.background = { color: COLORS.navy };
  if (logo) {
    s.addImage({ data: logo, x: 4.25, y: 0.4, w: 1.5, h: 1.5 });
  }
  s.addText("OFUESI", { x: 0.5, y: 2.0, w: 9, h: 0.4, fontSize: 16, bold: true, color: COLORS.gold, align: "center" });
  s.addText(opts.scope === "nation" ? "National Conference Report" : `${opts.region} Regional Conference Report`, {
    x: 0.5, y: 2.5, w: 9, h: 0.9, fontSize: 32, bold: true, color: COLORS.white, align: "center",
  });
  s.addText(`${opts.year} · ${opts.mode === "summary" ? "Executive Summary" : "Detailed Review"}`, {
    x: 0.5, y: 3.4, w: 9, h: 0.5, fontSize: 18, color: COLORS.gold, align: "center",
  });

  const lines: string[] = [];
  if (opts.cover?.presidentName) lines.push(`President: ${opts.cover.presidentName}`);
  if (opts.cover?.secretaryName) lines.push(`Secretary: ${opts.cover.secretaryName}`);
  if (opts.cover?.meetingDate) {
    const d = new Date(opts.cover.meetingDate);
    lines.push(`Business Meeting: ${isNaN(d.getTime()) ? opts.cover.meetingDate : d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}`);
  }
  // Regional coordinator(s)
  const coordLines = coordinators
    .filter((c) => c.name)
    .map((c) => `Coordinator${opts.scope === "nation" && c.region ? ` (${c.region})` : ""}: ${c.name}${c.email ? ` · ${c.email}` : ""}${c.phone ? ` · ${c.phone}` : ""}`);
  const allLines = [...lines, ...coordLines];
  if (allLines.length) {
    s.addText(allLines.join("\n"), {
      x: 0.5, y: 4.0, w: 9, h: 1.0, fontSize: 12, color: COLORS.white, align: "center",
    });
  }
  s.addText(
    `${OFUESI_WEBSITE} · Generated ${new Date().toLocaleDateString()}${opts.generatedByName ? " by " + opts.generatedByName : ""}`,
    { x: 0.5, y: 5.25, w: 9, h: 0.3, fontSize: 10, color: COLORS.gold, align: "center", italic: true },
  );
};

const sectionDivider = (pptx: pptxgen, title: string, subtitle: string | undefined, opts: Options, logo?: string) => {
  const s = pptx.addSlide();
  s.background = { color: COLORS.teal };
  s.addText(title, { x: 0.5, y: 2.2, w: 9, h: 0.9, fontSize: 32, bold: true, color: COLORS.white, align: "center" });
  if (subtitle) s.addText(subtitle, { x: 0.5, y: 3.2, w: 9, h: 0.5, fontSize: 16, color: COLORS.gold, align: "center" });
  addFooter(s, opts, logo, true);
};

const addFooter = (slide: pptxgen.Slide, opts: Options, logo?: string, onDark = false) => {
  const fg = onDark ? COLORS.white : COLORS.dark;
  if (logo) {
    try { slide.addImage({ data: logo, x: 0.2, y: 5.25, w: 0.3, h: 0.3 }); } catch { /* ignore */ }
  }
  slide.addText("OFUESI", { x: 0.55, y: 5.27, w: 1.2, h: 0.25, fontSize: 9, bold: true, color: fg });
  slide.addText(scopeLabel(opts), { x: 1.7, y: 5.27, w: 4, h: 0.25, fontSize: 9, color: fg });
  slide.addText(OFUESI_WEBSITE, { x: 7.5, y: 5.27, w: 2.3, h: 0.25, fontSize: 9, color: fg, align: "right", italic: true });
};

const contentSlide = (pptx: pptxgen, title: string, opts: Options, logo?: string) => {
  const s = pptx.addSlide();
  s.background = { color: COLORS.white };
  s.addShape("rect" as any, { x: 0, y: 0, w: 10, h: 0.55, fill: { color: COLORS.navy } });
  s.addText(title, { x: 0.4, y: 0.08, w: 9.2, h: 0.4, fontSize: 18, bold: true, color: COLORS.white });
  s.addShape("rect" as any, { x: 0, y: 5.2, w: 10, h: 0.05, fill: { color: COLORS.gold } });
  addFooter(s, opts, logo, false);
  return s;
};

const kpiRow = (slide: pptxgen.Slide, items: { label: string; value: string }[], y = 0.9) => {
  const w = 9.2 / items.length;
  items.forEach((kpi, i) => {
    const x = 0.4 + i * w;
    slide.addShape("rect" as any, { x, y, w: w - 0.15, h: 1.2, fill: { color: COLORS.light }, line: { color: COLORS.navy, width: 1 } });
    slide.addText(kpi.value, { x, y: y + 0.1, w: w - 0.15, h: 0.6, fontSize: 22, bold: true, color: COLORS.navy, align: "center" });
    slide.addText(kpi.label, { x, y: y + 0.75, w: w - 0.15, h: 0.4, fontSize: 10, color: COLORS.dark, align: "center" });
  });
};

const tableSlide = (pptx: pptxgen, title: string, headers: string[], rows: (string | number)[][], opts: Options, logo?: string) => {
  const PER = 14;
  for (let i = 0; i < Math.max(1, Math.ceil(rows.length / PER)); i++) {
    const slide = contentSlide(pptx, rows.length > PER ? `${title} (${i + 1})` : title, opts, logo);
    const chunk = rows.slice(i * PER, i * PER + PER);
    const tableData: any[] = [
      headers.map((h) => ({ text: h, options: { bold: true, color: COLORS.white, fill: { color: COLORS.navy }, align: "left" } })),
      ...chunk.map((r) => r.map((c) => ({ text: String(c ?? ""), options: { color: COLORS.dark } }))),
    ];
    slide.addTable(tableData, {
      x: 0.4, y: 0.75, w: 9.2, fontSize: 10, border: { type: "solid", pt: 0.5, color: "DDDDDD" },
    });
  }
};

const regionChartSlides = (pptx: pptxgen, agg: ReturnType<typeof aggregateByRegion>, opts: Options, logo?: string) => {
  if (!agg.length) return;
  const labels = agg.map((r) => r.region);

  // Registrations chart
  const s1 = contentSlide(pptx, "Registrations by Region", opts, logo);
  s1.addChart("bar" as any, [
    { name: "Registrations", labels, values: agg.map((r) => r.registrations) },
    { name: "Confirmed Paid", labels, values: agg.map((r) => r.confirmed) },
    { name: "Total Members", labels, values: agg.map((r) => r.members) },
  ], {
    x: 0.4, y: 0.8, w: 9.2, h: 4.2,
    barDir: "col", showLegend: true, legendPos: "b",
    catAxisLabelFontSize: 10, valAxisLabelFontSize: 10,
    chartColors: [COLORS.navy, COLORS.green, COLORS.gold],
  });

  // Utilization %
  const s2 = contentSlide(pptx, "Budget Utilization % by Region", opts, logo);
  s2.addChart("bar" as any, [{
    name: "Utilization %",
    labels,
    values: agg.map((r) => r.estimated > 0 ? Math.round((r.spent / r.estimated) * 100) : 0),
  }], {
    x: 0.4, y: 0.8, w: 9.2, h: 4.2,
    barDir: "col", showLegend: false, showValue: true,
    chartColors: [COLORS.amber], valAxisMaxVal: 120,
    catAxisLabelFontSize: 10, valAxisLabelFontSize: 10,
  });

  // Surplus / deficit
  const s3 = contentSlide(pptx, "Surplus / Deficit by Region", opts, logo);
  const surplusVals = agg.map((r) => Number((r.income - r.spent).toFixed(2)));
  s3.addChart("bar" as any, [{
    name: "Surplus / Deficit ($)",
    labels,
    values: surplusVals,
  }], {
    x: 0.4, y: 0.8, w: 9.2, h: 4.2,
    barDir: "col", showLegend: false, showValue: true,
    chartColors: [COLORS.teal],
    catAxisLabelFontSize: 10, valAxisLabelFontSize: 10,
  });
};

async function buildPptxBlob(opts: Options, data: Awaited<ReturnType<typeof loadData>>, logo: string | undefined, coordinators: CoordinatorInfo[]): Promise<Blob> {
  const { events, budget, expenses, submissions, magazine, publishLog, registrations, feedback } = data;

  const pptx = new pptxgen();
  pptx.defineLayout({ name: "LAYOUT_WIDE", width: 10, height: 5.625 });
  pptx.layout = "LAYOUT_WIDE";
  pptx.author = "OFUESI";
  pptx.title = `OFUESI ${opts.year} Conference Report`;

  titleSlide(pptx, opts, logo, coordinators);

  const totalReg = registrations.length;
  const totalAttendees = registrations.reduce((s, r) => s + (r.total_members || 1), 0);
  const confirmed = registrations.filter((r) => r.payment_status === "confirmed").length;
  const totalEstimated = budget.reduce((s, b) => s + Number(b.estimated_amount || 0), 0);
  const incomeBudget = budget.filter((b) => (b.category || "").toLowerCase().startsWith("income")).reduce((s, b) => s + Number(b.estimated_amount || 0), 0);
  const expenseBudget = totalEstimated - incomeBudget;
  const totalSpent = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const surplus = incomeBudget - totalSpent;

  const exec = contentSlide(pptx, "Executive Summary", opts, logo);
  kpiRow(exec, [
    { label: "Conferences", value: String(events.length) },
    { label: "Registrations", value: String(totalReg) },
    { label: "Total Attendees", value: String(totalAttendees) },
    { label: "Confirmed Paid", value: String(confirmed) },
  ], 0.85);
  kpiRow(exec, [
    { label: "Budget (Expense)", value: fmtMoney(expenseBudget) },
    { label: "Income Plan", value: fmtMoney(incomeBudget) },
    { label: "Spent", value: fmtMoney(totalSpent) },
    { label: surplus >= 0 ? "Surplus" : "Deficit", value: fmtMoney(Math.abs(surplus)) },
  ], 2.25);

  exec.addText(
    [
      { text: "Highlights\n", options: { bold: true, fontSize: 14, color: COLORS.navy } },
      { text: `• ${events.length} conference${events.length === 1 ? "" : "s"} ${opts.scope === "nation" ? "across regions" : `in ${opts.region}`}.\n`, options: { fontSize: 12 } },
      { text: `• ${feedback.length} feedback responses received.\n`, options: { fontSize: 12 } },
      { text: `• ${publishLog.length} publish actions logged (flyers, magazine, reports).\n`, options: { fontSize: 12 } },
      { text: `• Budget utilization: ${expenseBudget > 0 ? Math.round((totalSpent / expenseBudget) * 100) : 0}%.`, options: { fontSize: 12 } },
    ],
    { x: 0.4, y: 3.6, w: 9.2, h: 1.4, valign: "top" },
  );

  // Region-wise visuals
  const agg = aggregateByRegion(data);
  if (agg.length) {
    sectionDivider(pptx, "Region-wise KPIs", `${agg.length} region${agg.length === 1 ? "" : "s"}`, opts, logo);
    regionChartSlides(pptx, agg, opts, logo);
  }

  sectionDivider(pptx, "Conferences", `${events.length} event${events.length === 1 ? "" : "s"} in ${opts.year}`, opts, logo);
  tableSlide(pptx, "Conferences Overview",
    ["Region", "Title", "Theme", "Dates", "Venue", "Status"],
    events.map((e: any) => [
      e.region || "", e.title || "", e.theme || "",
      [e.start_date, e.end_date].filter(Boolean).join(" → "),
      [e.venue_name, e.city, e.state].filter(Boolean).join(", "),
      e.status || "",
    ]), opts, logo);

  sectionDivider(pptx, "Registrations & Attendance", undefined, opts, logo);
  tableSlide(pptx, "Registrations by Region",
    ["Region", "Registrations", "Total Members", "Confirmed Paid"],
    agg.map((v) => [v.region, v.registrations, v.members, v.confirmed]), opts, logo);

  sectionDivider(pptx, "Budget & Finance", surplus >= 0 ? `Surplus ${fmtMoney(surplus)}` : `Deficit ${fmtMoney(Math.abs(surplus))}`, opts, logo);
  tableSlide(pptx, "Budget Lines",
    ["Category", "Description", "Estimated", "Actual", "Variance"],
    budget.map((b) => {
      const est = Number(b.estimated_amount || 0);
      const act = Number(b.actual_amount || 0);
      return [b.category || "", b.description || "", fmtMoney(est), fmtMoney(act), fmtMoney(est - act)];
    }), opts, logo);

  if (opts.mode === "detailed") {
    tableSlide(pptx, "Expense Detail",
      ["Date", "Vendor", "Description", "Amount", "Status"],
      expenses.map((e) => [e.expense_date || "", e.vendor || "", e.description || "", fmtMoney(Number(e.amount || 0)), e.reimbursement_status || ""]), opts, logo);
  }

  sectionDivider(pptx, "KIT Magazine & Highlights", undefined, opts, logo);
  if (magazine.length === 0) {
    const s = contentSlide(pptx, "KIT Magazine Submissions", opts, logo);
    s.addText("No KIT magazine submissions on file for this period.", { x: 0.4, y: 1.5, w: 9.2, h: 0.5, fontSize: 14, italic: true, color: COLORS.dark });
  } else {
    magazine.forEach((m) => {
      const s = contentSlide(pptx, `KIT Magazine — ${m.region || ""}`, opts, logo);
      s.addText(
        [
          { text: `${m.conference_title || ""}\n`, options: { bold: true, fontSize: 16, color: COLORS.navy } },
          { text: `${m.theme_and_scripture || ""}\n\n`, options: { italic: true, fontSize: 12, color: COLORS.dark } },
          { text: `Attendance: ${m.total_attendance || 0} (Adults ${m.total_adults || 0} · Youth ${m.total_youth || 0} · Children ${m.total_children || 0})\n`, options: { fontSize: 12 } },
          { text: `Churches Represented: ${m.churches_represented || 0}\n\n`, options: { fontSize: 12 } },
          { text: "Key Outcomes\n", options: { bold: true, fontSize: 13, color: COLORS.teal } },
          { text: `${m.key_outcomes || "—"}\n\n`, options: { fontSize: 11 } },
          { text: "Narrative\n", options: { bold: true, fontSize: 13, color: COLORS.teal } },
          { text: m.narrative || "—", options: { fontSize: 11 } },
        ],
        { x: 0.4, y: 0.75, w: 9.2, h: 4.3, valign: "top" },
      );
    });
  }

  if (submissions.length) {
    tableSlide(pptx, "Submissions & Articles",
      ["Type", "File", "Status", "Submitted", "Deadline"],
      submissions.map((s) => [s.submission_type || "", s.file_name || "", s.status || "", s.submitted_at?.slice(0, 10) || "", s.deadline_date || ""]), opts, logo);
  }

  sectionDivider(pptx, "Feedback Summary", `${feedback.length} responses`, opts, logo);
  if (feedback.length) {
    const avg = (key: string) => {
      const vals = feedback.map((f) => Number(f[key])).filter((n) => !isNaN(n) && n > 0);
      return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2) : "—";
    };
    const f = contentSlide(pptx, "Feedback Ratings (Avg / 5)", opts, logo);
    kpiRow(f, [
      { label: "Program", value: String(avg("rating_program")) },
      { label: "Speakers", value: String(avg("rating_speakers")) },
      { label: "Venue", value: String(avg("rating_venue")) },
      { label: "Food", value: String(avg("rating_food")) },
    ], 0.9);
    kpiRow(f, [{ label: "Time-keeping", value: String(avg("rating_timekeeping")) }], 2.3);

    if (opts.mode === "detailed") {
      const suggestions = feedback.map((x) => x.suggestions).filter(Boolean).slice(0, 25);
      if (suggestions.length) {
        tableSlide(pptx, "Top Suggestions", ["#", "Suggestion"], suggestions.map((s, i) => [i + 1, s]), opts, logo);
      }
    }
  }

  if (opts.mode === "detailed" && publishLog.length) {
    tableSlide(pptx, "Publish Activity",
      ["Date", "Item", "Audience", "By", "Status"],
      publishLog.map((p) => [p.published_at?.slice(0, 10) || "", p.item_type || "", p.audience || "", p.published_by_name || "", p.status || ""]), opts, logo);
  }

  const actions = contentSlide(pptx, "Actions & Decisions for Future Conferences", opts, logo);
  actions.addText(
    [
      { text: "Recommended Actions\n", options: { bold: true, fontSize: 16, color: COLORS.navy } },
      { text: "• Lock budget approvals 60 days before each conference.\n", options: { fontSize: 12 } },
      { text: "• Publish flyer at least 90 days in advance for early-bird registrations.\n", options: { fontSize: 12 } },
      { text: "• Standardise KIT magazine submissions (5–8 photos, narrative + outcomes) within 30 days post-conference.\n", options: { fontSize: 12 } },
      { text: "• Review feedback ratings below 3.5 and assign owner per category.\n", options: { fontSize: 12 } },
      { text: "• Carry forward surplus / fund deficit per President + Secretary decision.\n\n", options: { fontSize: 12 } },
      { text: "Decisions Required (to be filled in Business Meeting)\n", options: { bold: true, fontSize: 16, color: COLORS.navy } },
      { text: "1. ____________________________________________\n", options: { fontSize: 12, color: COLORS.dark } },
      { text: "2. ____________________________________________\n", options: { fontSize: 12, color: COLORS.dark } },
      { text: "3. ____________________________________________", options: { fontSize: 12, color: COLORS.dark } },
    ],
    { x: 0.4, y: 0.8, w: 9.2, h: 4.2, valign: "top" },
  );

  const close = pptx.addSlide();
  close.background = { color: COLORS.navy };
  if (logo) {
    try { close.addImage({ data: logo, x: 4.4, y: 0.5, w: 1.2, h: 1.2 }); } catch { /* ignore */ }
  }
  close.addText("Thank You", { x: 0.5, y: 2.0, w: 9, h: 1, fontSize: 40, bold: true, color: COLORS.white, align: "center" });
  close.addText('"Let all things be done decently and in order." — 1 Corinthians 14:40', {
    x: 0.5, y: 3.2, w: 9, h: 0.6, fontSize: 14, italic: true, color: COLORS.gold, align: "center",
  });
  close.addText(`OFUESI · ${scopeLabel(opts)} · ${OFUESI_WEBSITE}`, {
    x: 0.5, y: 4.4, w: 9, h: 0.4, fontSize: 12, color: COLORS.gold, align: "center", italic: true,
  });

  const blob = (await pptx.write({ outputType: "blob" })) as Blob;
  return blob;
}

function pdfFooter(doc: jsPDF, opts: Options, logo?: string) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  if (logo) {
    try { doc.addImage(logo, "PNG", 36, H - 36, 18, 18); } catch { /* ignore */ }
  }
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`OFUESI · ${scopeLabel(opts)}`, 60, H - 22);
  doc.text(OFUESI_WEBSITE, W - 36, H - 22, { align: "right" });
}

function buildPdfBlob(opts: Options, data: Awaited<ReturnType<typeof loadData>>, logo: string | undefined, coordinators: CoordinatorInfo[]): Blob {
  const { events, budget, expenses, registrations, feedback, publishLog } = data;
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();

  // cover
  doc.setFillColor(26, 58, 95);
  doc.rect(0, 0, W, H, "F");
  if (logo) {
    try { doc.addImage(logo, "PNG", W / 2 - 50, 50, 100, 100); } catch { /* ignore */ }
  }
  doc.setTextColor(201, 168, 76);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text("OFUESI", W / 2, 180, { align: "center" });
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.text(opts.scope === "nation" ? "National Conference Report" : `${opts.region} Regional Conference Report`, W / 2, 230, { align: "center" });
  doc.setFontSize(16);
  doc.setTextColor(201, 168, 76);
  doc.text(`${opts.year} · ${opts.mode === "summary" ? "Executive Summary" : "Detailed Review"}`, W / 2, 270, { align: "center" });

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(13);
  let y = 320;
  if (opts.cover?.presidentName) { doc.text(`President: ${opts.cover.presidentName}`, W / 2, y, { align: "center" }); y += 22; }
  if (opts.cover?.secretaryName) { doc.text(`Secretary: ${opts.cover.secretaryName}`, W / 2, y, { align: "center" }); y += 22; }
  if (opts.cover?.meetingDate) {
    const d = new Date(opts.cover.meetingDate);
    const ds = isNaN(d.getTime()) ? opts.cover.meetingDate : d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
    doc.text(`Business Meeting: ${ds}`, W / 2, y, { align: "center" }); y += 22;
  }
  doc.setFontSize(11);
  coordinators.filter(c => c.name).forEach((c) => {
    const tag = opts.scope === "nation" && c.region ? ` (${c.region})` : "";
    doc.text(`Coordinator${tag}: ${c.name}${c.email ? " · " + c.email : ""}${c.phone ? " · " + c.phone : ""}`, W / 2, y, { align: "center" });
    y += 18;
  });
  doc.setFontSize(10);
  doc.setTextColor(201, 168, 76);
  doc.text(`${OFUESI_WEBSITE} · Generated ${new Date().toLocaleDateString()}${opts.generatedByName ? " by " + opts.generatedByName : ""}`, W / 2, H - 40, { align: "center" });

  // exec summary page
  doc.addPage();
  doc.setTextColor(26, 58, 95);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("Executive Summary", 40, 50);

  const totalReg = registrations.length;
  const totalAttendees = registrations.reduce((s, r) => s + (r.total_members || 1), 0);
  const confirmed = registrations.filter((r) => r.payment_status === "confirmed").length;
  const totalEstimated = budget.reduce((s, b) => s + Number(b.estimated_amount || 0), 0);
  const incomeBudget = budget.filter((b) => (b.category || "").toLowerCase().startsWith("income")).reduce((s, b) => s + Number(b.estimated_amount || 0), 0);
  const expenseBudget = totalEstimated - incomeBudget;
  const totalSpent = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const surplus = incomeBudget - totalSpent;

  autoTable(doc, {
    startY: 70,
    head: [["Metric", "Value"]],
    body: [
      ["Conferences", String(events.length)],
      ["Registrations", String(totalReg)],
      ["Total Attendees", String(totalAttendees)],
      ["Confirmed Paid", String(confirmed)],
      ["Budget (Expense)", fmtMoney(expenseBudget)],
      ["Income Plan", fmtMoney(incomeBudget)],
      ["Spent", fmtMoney(totalSpent)],
      [surplus >= 0 ? "Surplus" : "Deficit", fmtMoney(Math.abs(surplus))],
      ["Utilization %", `${expenseBudget > 0 ? Math.round((totalSpent / expenseBudget) * 100) : 0}%`],
      ["Feedback Responses", String(feedback.length)],
    ],
    headStyles: { fillColor: [26, 58, 95] },
  });

  // region-wise
  const agg = aggregateByRegion(data);
  if (agg.length) {
    doc.addPage();
    doc.setFont("helvetica", "bold"); doc.setFontSize(18); doc.setTextColor(26, 58, 95);
    doc.text("Region-wise KPIs", 40, 50);
    autoTable(doc, {
      startY: 70,
      head: [["Region", "Registrations", "Confirmed", "Members", "Estimated", "Spent", "Util %", "Surplus/Deficit"]],
      body: agg.map((r) => [
        r.region, r.registrations, r.confirmed, r.members,
        fmtMoney(r.estimated), fmtMoney(r.spent),
        `${r.estimated > 0 ? Math.round((r.spent / r.estimated) * 100) : 0}%`,
        fmtMoney(r.income - r.spent),
      ]),
      headStyles: { fillColor: [26, 58, 95] },
    });
  }

  // budget detail
  if (budget.length) {
    doc.addPage();
    doc.setFont("helvetica", "bold"); doc.setFontSize(18); doc.setTextColor(26, 58, 95);
    doc.text("Budget Lines", 40, 50);
    autoTable(doc, {
      startY: 70,
      head: [["Category", "Description", "Estimated", "Actual", "Variance"]],
      body: budget.map((b) => {
        const est = Number(b.estimated_amount || 0);
        const act = Number(b.actual_amount || 0);
        return [b.category || "", b.description || "", fmtMoney(est), fmtMoney(act), fmtMoney(est - act)];
      }),
      headStyles: { fillColor: [26, 58, 95] },
    });
  }

  if (opts.mode === "detailed" && expenses.length) {
    doc.addPage();
    doc.setFont("helvetica", "bold"); doc.setFontSize(18); doc.setTextColor(26, 58, 95);
    doc.text("Expense Detail", 40, 50);
    autoTable(doc, {
      startY: 70,
      head: [["Date", "Vendor", "Description", "Amount", "Status"]],
      body: expenses.map((e) => [e.expense_date || "", e.vendor || "", e.description || "", fmtMoney(Number(e.amount || 0)), e.reimbursement_status || ""]),
      headStyles: { fillColor: [26, 58, 95] },
    });
  }

  if (opts.mode === "detailed" && publishLog.length) {
    doc.addPage();
    doc.setFont("helvetica", "bold"); doc.setFontSize(18); doc.setTextColor(26, 58, 95);
    doc.text("Publish Activity", 40, 50);
    autoTable(doc, {
      startY: 70,
      head: [["Date", "Item", "Audience", "By", "Status"]],
      body: publishLog.map((p) => [p.published_at?.slice(0, 10) || "", p.item_type || "", p.audience || "", p.published_by_name || "", p.status || ""]),
      headStyles: { fillColor: [26, 58, 95] },
    });
  }

  // Add footer to every page
  const pageCount = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) { doc.setPage(i); pdfFooter(doc, opts, logo); }
  return doc.output("blob");
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = fileName;
  document.body.appendChild(a); a.click();
  setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
}

export async function generateConferenceReport(opts: Options): Promise<{ fileName: string; publicUrl?: string }> {
  const data = await loadData(opts);
  const format: ReportFormat = opts.format || "pptx";
  const safeRegion = opts.scope === "nation" ? "National" : (opts.region?.replace(/\s+/g, "-") || "Region");
  const fileName = `OFUESI-${safeRegion}-Conference-${opts.mode}-${opts.year}.${format}`;

  const logo = await ensureLogo(opts.cover);
  const coordinators: CoordinatorInfo[] = (data.events || [])
    .map((e: any) => ({
      name: e.coordinator_name || undefined,
      email: e.coordinator_email || undefined,
      phone: e.coordinator_phone || undefined,
      region: e.region || undefined,
    }))
    .filter((c, idx, arr) => c.name && arr.findIndex((x) => x.name === c.name && x.region === c.region) === idx);

  const blob = format === "pdf"
    ? buildPdfBlob(opts, data, logo, coordinators)
    : await buildPptxBlob(opts, data, logo, coordinators);

  // Trigger download
  downloadBlob(blob, fileName);

  // Archive (default true)
  if (opts.archive !== false) {
    try {
      const path = `${opts.year}/${opts.scope}/${safeRegion}/${Date.now()}-${fileName}`;
      const { error: upErr } = await supabase.storage.from("conference-reports").upload(path, blob, {
        contentType: format === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        upsert: false,
      });
      if (!upErr) {
        const { data: pub } = supabase.storage.from("conference-reports").getPublicUrl(path);
        await (supabase as any).from("conference_report_archive").insert({
          conference_year: opts.year,
          scope: opts.scope,
          region: opts.scope === "region" ? opts.region : null,
          mode: opts.mode,
          format,
          file_name: fileName,
          storage_path: path,
          public_url: pub?.publicUrl,
          president_name: opts.cover?.presidentName || null,
          secretary_name: opts.cover?.secretaryName || null,
          meeting_date: opts.cover?.meetingDate || null,
          generated_by: opts.generatedById || null,
          generated_by_name: opts.generatedByName || null,
        });
        return { fileName, publicUrl: pub?.publicUrl };
      }
    } catch (e) {
      console.warn("Archive failed", e);
    }
  }

  return { fileName };
}
